import React, { useState } from 'react';
import { DraggableProvided } from 'react-beautiful-dnd';
import WidgetModel from '@mm/state/model/WidgetModel';
import { ReactComponent as CloseIcon } from 'assets/close-icon.svg';
import './GifWidget.pcss';
import { FacebookPageIcon } from '../common/FacebookPageIcon/FacebookPageIcon';
import { WidgetControls } from '../common/WidetControls/WidgetControls';
import { useCommonWidgetHandlers } from '../../helpers/widget-helpers/use-common-widget-handlers';
import { GifSearch } from './GifSearch/GifSearch';
import { GifResponse } from '@mm/state/controllers/GifController';
import { useGifActions } from '@mm/state/actions/GifActions';
import { WidgetContainerRefProp } from '../../../editor/features/startBotPromoters/common/StartBotsDialogueEditor/WidgetByType/WidgetByType';
import { Alert } from '@mm/components';
import { PlatformType } from '@mm/state/model/CampaignsModel';
import PoweredByGiphy from 'assets/PoweredByGiphy.png';

type Props = {
  widget: WidgetModel;
  draggableProvided?: DraggableProvided;
  isSmsRelated?: boolean;
  smsOnly?: boolean;
  platforms?: PlatformType[];
} & WidgetContainerRefProp;

const SMS_SIZE_CAP = 5 * 1024 * 1024;

export const GifWidget = (props: Props) => {
  const { widget, draggableProvided, isSmsRelated, smsOnly, platforms } = props;
  const [uploadingGif, setUploadingGif] = useState(false);
  const [uploadingGifSizes, setUploadingGifSizes] = useState({
    width: '0',
    height: '0',
  });
  const commonWidgetHandlers = useCommonWidgetHandlers();
  const gifActions = useGifActions();
  const image = widget.file;

  const selectGif = async (selectedGif: GifResponse) => {
    setUploadingGif(true);
    setUploadingGifSizes({
      width: selectedGif.images.downsized.width,
      height: selectedGif.images.downsized.height,
    });

    if (selectedGif && selectedGif.images) {
      const assetId = await gifActions.getGifFromUrl(
        selectedGif.images.downsized.url,
      );

      if (assetId) {
        await commonWidgetHandlers.updateAssetId(widget, assetId);
        setUploadingGif(false);
      }
    }
  };

  const removeGif = () => commonWidgetHandlers.updateAssetId(widget, null);

  const uploadingStyles = {
    width: `${uploadingGifSizes.width}px`,
    height: `${uploadingGifSizes.height}px`,
  };

  const { sourceSize } = widget.config;
  const isSizeOvercap = isSmsRelated && sourceSize && sourceSize > SMS_SIZE_CAP;

  return (
    <div className="gif-widget">
      <FacebookPageIcon />

      <div className="gif-widget__main-area">
        {image && widget.config.assetId && (
          <div className="gif-widget__preview-wrapper">
            <img src={image} className="gif-widget__preview-image" />

            <div className="gif-widget__remove-wrapper">
              <div className="gif-widget__remove-blinder" />

              <div className="gif-widget__remove-text" onClick={removeGif}>
                <CloseIcon className="gif-widget__close-icon" />
                <span>Remove GIF</span>
              </div>
            </div>
          </div>
        )}

        {uploadingGif && (
          <div className="gif-widget__gif-uploading" style={uploadingStyles}>
            <span>Processing...</span>
          </div>
        )}

        {!uploadingGif && !widget.config.assetId && (
          <GifSearch selectGif={selectGif} />
        )}

        <img
          className="gif-widget__giphy-attribute"
          alt="Powered By Giphy"
          src={PoweredByGiphy}
        />
      </div>

      {isSizeOvercap && (
        <Alert
          className="gif-widget__warning"
          type={smsOnly ? 'error' : 'warning'}
          showCloseIcon={false}
        >
          This media file exceeds size limit on SMS and will NOT be sent in your
          SMS dialogue. Please reduce size to &lt;5MB.
        </Alert>
      )}

      <WidgetControls
        widget={widget}
        draggableProvided={draggableProvided}
        platforms={platforms}
      />
    </div>
  );
};
